/**
 * Treasure chests (GDD section 9). At the start of each turn a chest comes down by parachute above a random
 * point of dry land; it drifts with the wind, and its drop point is corrected so that it never lands in water.
 * Once landed it is a physical object: blasts throw it, sand buries it. The active unit opens it by touching
 * it. Lava destroys it after 3 s. Pushed into a liquid, its parachute opens again and it lands on the nearest
 * shore. At most 3 chests on the map. One drop in ten is a mimic.
 */
import { Physique, STATE_LIQUID, nextInt, isqrt } from '@invaders/sim';
import {
  FP, FP_SHIFT, TICKS_PER_SECOND, CONTENT_MEDKIT, CHEST_FALLING, CHEST_GROUND, CHEST_OPEN,
  type Chest, type GameState, type Unit,
} from './state';
import { UNIT_H, UNIT_W, PAD_VY, healUnit } from './unit';
import { WEAPONS } from './weapons';
import { WEAPON_ACID_BARREL, WEAPON_COCKTAIL, WEAPON_CRYO, WEAPON_DOLL, WEAPON_GEYSER, WEAPON_LASER, WEAPON_METEOR, WEAPON_POWDER_BARREL, WEAPON_REMOTE, WEAPON_ROOMBA, WEAPON_VOLCANO } from './weapon-ids';
import { damageUnit } from './unit';

export const CHEST_W = 10;
export const CHEST_H = 10;
export const MAX_CHESTS = 3;
/** Under its parachute: half a cell per tick down, and the wind's drift sideways. */
export const PARACHUTE_VY = FP >> 1;
/** Sideways drift per point of wind, per tick (fixed-point FP). */
export const CHEST_DRIFT = 8;
/** GDD: its reinforcements hold 3 s in fire or lava. */
export const CHEST_HEAT_TICKS = 3 * TICKS_PER_SECOND;
export const MEDKIT_HEAL = 25;
/** Back on its parachute after falling into a liquid: flies to the shore at 1 cell per tick. */
export const CHEST_SHORE = 3;
/** A mimic that bit someone runs away hopping, to hide elsewhere on the island. */
export const CHEST_HOP = 4;
/** GDD: one drop in ten is a mimic; it bites for 25. */
export const MIMIC_CHANCE = 10;
export const MIMIC_BITE = 25;
/** It runs at least this far before hiding. */
const MIMIC_RUN = 150;
/**
 * A run is MIMIC_HOPS hops at most (enough to cross the map); a hop stopped by a wall leaves it MIMIC_STUCK_HOPS more
 * tries before it hides where it is (author's fix: it could hop forever against a cliff, or to and fro past its target).
 */
export const MIMIC_HOPS = 40;
export const MIMIC_STUCK_HOPS = 2;
/** Take-off speed of a hop (fixed-point FP): about 32 cells high. */
const MIMIC_HOP_VY = 2 * FP;
/** Sandbox: back at rest after a run, it does not bite again for this many ticks (a match: until the next turn). */
const MIMIC_REST_TICKS = 3 * TICKS_PER_SECOND;
/** GDD: destroyed by an explosion, a mimic drops a rare weapon. */
export const RARE_WEAPONS: readonly number[] = [WEAPON_LASER, WEAPON_GEYSER, WEAPON_DOLL];
const GRAVITY = 16;
const MAX_FALL = 6 * FP;

/** Chest table of the GDD with the contents built so far; weights in percent. */
export const CHEST_TABLE: readonly { content: number; weight: number }[] = [
  { content: CONTENT_MEDKIT, weight: 10 },
  // The drill and the mines are base weapons now (author's choice): they left the chests, the rest by the same weights.
  { content: WEAPON_COCKTAIL, weight: 12 },
  { content: WEAPON_CRYO, weight: 12 },
  { content: WEAPON_METEOR, weight: 5 },
  { content: WEAPON_LASER, weight: 12 },
  { content: WEAPON_GEYSER, weight: 8 },
  { content: WEAPON_DOLL, weight: 8 },
  { content: WEAPON_REMOTE, weight: 5 },
  // Author's additions; with them the weights add up to 100 again.
  { content: WEAPON_ACID_BARREL, weight: 7 },
  { content: WEAPON_POWDER_BARREL, weight: 7 },
  { content: WEAPON_VOLCANO, weight: 8 },
  { content: WEAPON_ROOMBA, weight: 6 },
];

export function contentName(content: number): string {
  return content === CONTENT_MEDKIT ? 'Medkit' : WEAPONS[content]?.name ?? '?';
}

function boxFree(state: GameState, cx: number, cy: number): boolean {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  if (cx < 0 || cx + CHEST_W > w) return false;
  for (let y = Math.max(0, cy); y < Math.min(h, cy + CHEST_H); y++) {
    for (let x = cx; x < cx + CHEST_W; x++) if (matState[data[y * w + x]] < STATE_LIQUID) return false;
  }
  return true;
}

function centerLiquid(state: GameState, c: Chest): boolean {
  const { w, h, data } = state.world.physique;
  const x = (c.x >> FP_SHIFT) + (CHEST_W >> 1);
  const y = (c.y >> FP_SHIFT) + (CHEST_H >> 1);
  return x >= 0 && x < w && y >= 0 && y < h && state.world.rules.materials.state[data[y * w + x]] === STATE_LIQUID;
}

/** First cell that is not a gas in column x from row y down, and whether it is a liquid; -1 if none. */
function surfaceBelow(state: GameState, x: number, y: number): [number, boolean] {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  for (let yy = Math.max(0, y); yy < h; yy++) {
    const s = matState[data[yy * w + x]];
    if (s <= STATE_LIQUID) return [yy, s === STATE_LIQUID];
  }
  return [-1, false];
}

/** True if a chest may hold `content`: the medkit always, a weapon if the host left it in the chests. */
function allowed(state: GameState, content: number): boolean {
  const list = state.match?.config.chestWeapons;
  return content === CONTENT_MEDKIT || !list || list.includes(content);
}

/** A content from the chest table, by weight (-1 if the host left nothing a chest may hold). */
export function pickContent(state: GameState): number {
  const lava = (state.match?.lavaLevel ?? 0) > 0;
  // GDD: once the lava rises, chests no longer contain medkits.
  const table = CHEST_TABLE.filter((e) => !(lava && e.content === CONTENT_MEDKIT) && allowed(state, e.content));
  if (table.length === 0) return -1;
  let total = 0;
  for (const e of table) total += e.weight;
  let r = nextInt(state.world.rng, total);
  for (const e of table) { if (r < e.weight) return e.content; r -= e.weight; }
  return table[0].content;
}

/** Drop column (left cell of the box) so that the drift of `wind` brings the chest onto column `tx`. */
function dropColumn(state: GameState, tx: number, landY: number): number {
  const ticks = Math.trunc(((landY - CHEST_H) << FP_SHIFT) / PARACHUTE_VY);
  return tx - Math.trunc((state.wind * CHEST_DRIFT * ticks) / FP);
}

/** Simulates the parachute descent from (x, 0): the landing row, or -1 if it would land in a liquid or leave the map. */
function landing(state: GameState, x: number): number {
  let fx = x << FP_SHIFT;
  let fy = 0;
  const vx = state.wind * CHEST_DRIFT;
  for (let t = 0; t < 4000; t++) {
    const cx = fx >> FP_SHIFT;
    const cy = fy >> FP_SHIFT;
    if (cx < 0 || cx + CHEST_W > state.world.physique.w) return -1;
    if (!boxFree(state, cx, cy + 1)) {
      const [, liquid] = surfaceBelow(state, cx + (CHEST_W >> 1), cy + CHEST_H);
      return liquid ? -1 : cy;
    }
    const [sy, liquid] = surfaceBelow(state, cx + (CHEST_W >> 1), cy + CHEST_H);
    if (liquid && sy <= cy + CHEST_H) return -1;
    fx += vx;
    fy += PARACHUTE_VY;
  }
  return -1;
}

/** Start of a turn: a new chest, unless 3 are already on the map or no landing on dry land is found. */
export function dropChest(state: GameState, content = pickContent(state)): Chest | null {
  if (state.chests.length >= MAX_CHESTS || content < 0) return null;
  const w = state.world.physique.w;
  const rng = state.world.rng;
  for (let attempt = 0; attempt < 32; attempt++) {
    const tx = 8 + nextInt(rng, w - CHEST_W - 16);
    const [sy, liquid] = surfaceBelow(state, tx + (CHEST_W >> 1), 0);
    if (sy < CHEST_H || liquid) continue;
    const x = dropColumn(state, tx, sy);
    if (landing(state, x) < 0) continue;
    return spawnChest(state, x, content, nextInt(rng, MIMIC_CHANCE) === 0);
  }
  return null;
}

/** Puts a chest under its parachute at the top of column x (also the sandbox tool). */
/**
 * A ghost turn's chest (author's addition): from the top of the map over column x (its centre), with a content of the
 * table and the same chance of a mimic; null if 3 chests are already on the map or nothing may be in it.
 */
export function ghostChest(state: GameState, x: number): Chest | null {
  const content = pickContent(state);
  if (state.chests.length >= MAX_CHESTS || content < 0) return null;
  const cx = Math.min(state.world.physique.w - CHEST_W, Math.max(0, Math.trunc(x) - (CHEST_W >> 1)));
  return spawnChest(state, cx, content, nextInt(state.world.rng, MIMIC_CHANCE) === 0);
}

export function spawnChest(state: GameState, x: number, content: number, mimic = false): Chest {
  const c: Chest = { id: state.nextChest++, x: x << FP_SHIFT, y: 0, vx: 0, vy: PARACHUTE_VY, mode: CHEST_FALLING, content, heat: 0, until: 0, hops: 0, mimic };
  state.chests.push(c);
  state.events.push({ type: 'chest', chest: c.id, what: 'drop', content, unit: -1, x, y: 0 });
  return c;
}

function touchesUnit(c: Chest, u: Unit): boolean {
  const cx = c.x >> FP_SHIFT;
  const cy = c.y >> FP_SHIFT;
  const ux = u.x >> FP_SHIFT;
  const uy = u.y >> FP_SHIFT;
  return cx <= ux + UNIT_W && ux <= cx + CHEST_W && cy <= uy + UNIT_H && uy <= cy + CHEST_H;
}

/**
 * The mimic bites the unit that opens it, then runs away hopping to a far point of dry land (none found: away from the
 * unit, MIMIC_RUN cells).
 */
function bite(state: GameState, c: Chest, u: Unit): void {
  damageUnit(state, u, MIMIC_BITE, 'direct');
  state.events.push({ type: 'bite', chest: c.id, unit: u.id, x: c.x >> FP_SHIFT, y: c.y >> FP_SHIFT });
  const w = state.world.physique.w;
  const cx = c.x >> FP_SHIFT;
  const away = cx >= (u.x >> FP_SHIFT) ? 1 : -1;
  let target = Math.min(w - CHEST_W - 8, Math.max(8, cx + away * MIMIC_RUN));
  for (let attempt = 0; attempt < 32; attempt++) {
    const tx = 8 + nextInt(state.world.rng, w - CHEST_W - 16);
    const [sy, liquid] = surfaceBelow(state, tx + (CHEST_W >> 1), 0);
    if (Math.abs(tx - cx) >= MIMIC_RUN && sy >= CHEST_H && !liquid) { target = tx; break; }
  }
  c.mode = CHEST_HOP;
  c.until = target;
  c.hops = MIMIC_HOPS;
}

/** The run is over (there, given up, or in a liquid): it rests, and does not bite again before the next turn. */
function endRun(state: GameState, c: Chest): void {
  c.hops = 0;
  c.until = state.match ? state.match.turn : state.tick + MIMIC_REST_TICKS;
}

/** A mimic back at rest after a run: it does not bite during the same turn (it would bite the same unit in a loop). */
function resting(state: GameState, c: Chest): boolean {
  return c.mimic && c.until > 0 && (state.match ? c.until >= state.match.turn : state.tick < c.until);
}

function give(state: GameState, c: Chest, u: Unit): void {
  if (c.content === CONTENT_MEDKIT) healUnit(u, MEDKIT_HEAL);
  else if (state.match) {
    const slot = u.player * WEAPONS.length + c.content;
    if (state.match.ammo[slot] >= 0) state.match.ammo[slot]++;
  }
  state.events.push({ type: 'chest', chest: c.id, what: 'open', content: c.content, unit: u.id, x: c.x >> FP_SHIFT, y: c.y >> FP_SHIFT });
}

/** The unit that may open a closed chest: the unit of the turn in a match, any unit in the sandbox. */
function mayOpen(state: GameState, u: Unit, c: Chest): boolean {
  if (!u.alive) return false;
  if (c.mode === CHEST_OPEN || !state.match) return true;
  return u.id === state.match.selected && state.match.locked;
}

function moveVertical(state: GameState, c: Chest): void {
  const target = c.y + c.vy;
  const cx = c.x >> FP_SHIFT;
  let cy = c.y >> FP_SHIFT;
  const targetCell = target >> FP_SHIFT;
  const dir = targetCell > cy ? 1 : -1;
  while (cy !== targetCell) {
    if (!boxFree(state, cx, cy + dir)) {
      c.y = cy << FP_SHIFT;
      c.vy = 0;
      if (dir > 0) { c.vx = 0; if (c.mode === CHEST_FALLING) c.mode = CHEST_GROUND; }
      return;
    }
    cy += dir;
  }
  c.y = target;
}

/** True if a wall stopped it. */
function moveHorizontal(state: GameState, c: Chest): boolean {
  if (c.vx === 0) return false;
  const target = c.x + c.vx;
  let cx = c.x >> FP_SHIFT;
  const cy = c.y >> FP_SHIFT;
  const targetCell = target >> FP_SHIFT;
  const dir = targetCell > cx ? 1 : -1;
  while (cx !== targetCell) {
    if (!boxFree(state, cx + dir, cy)) { c.x = cx << FP_SHIFT; c.vx = 0; return true; }
    cx += dir;
  }
  c.x = target;
  return false;
}

/** Nearest column (left cell of a box) whose surface is dry land, searching both ways from x. */
function nearestShore(state: GameState, x: number): number {
  const w = state.world.physique.w;
  for (let d = 0; d < w; d++) {
    for (const nx of [x - d, x + d]) {
      if (nx < 0 || nx + CHEST_W > w) continue;
      const [sy, liquid] = surfaceBelow(state, nx + (CHEST_W >> 1), 0);
      if (sy >= CHEST_H && !liquid) return nx;
    }
  }
  return x;
}

function touchesMat(state: GameState, c: Chest, mat: number): boolean {
  const { w, h, data } = state.world.physique;
  const cx = c.x >> FP_SHIFT;
  const cy = c.y >> FP_SHIFT;
  for (let y = Math.max(0, cy - 1); y <= Math.min(h - 1, cy + CHEST_H); y++) {
    for (let x = Math.max(0, cx - 1); x <= Math.min(w - 1, cx + CHEST_W); x++) if (data[y * w + x] === mat) return true;
  }
  return false;
}

function touchesLava(state: GameState, c: Chest): boolean {
  const { w, h, data } = state.world.physique;
  const cx = c.x >> FP_SHIFT;
  const cy = c.y >> FP_SHIFT;
  for (let y = Math.max(0, cy - 1); y <= Math.min(h - 1, cy + CHEST_H); y++) {
    for (let x = Math.max(0, cx - 1); x <= Math.min(w - 1, cx + CHEST_W); x++) {
      const m = data[y * w + x];
      if (m === Physique.LAVA || m === Physique.FIRE) return true;
    }
  }
  return false;
}

export function updateChests(state: GameState): void {
  const h = state.world.physique.h;
  for (let i = state.chests.length - 1; i >= 0; i--) {
    const c = state.chests[i];
    let gone = false;
    if (c.mode === CHEST_FALLING) {
      c.vx = state.wind * CHEST_DRIFT;
      c.vy = PARACHUTE_VY;
    } else if (c.mode === CHEST_SHORE) {
      // Back on its parachute: up out of the liquid, then along to the shore, then down.
      const cx = c.x >> FP_SHIFT;
      const target = nearestShore(state, cx);
      const dir = target > cx ? 1 : -1;
      if (centerLiquid(state, c) || !boxFree(state, cx, (c.y >> FP_SHIFT))) { c.vx = 0; c.vy = -PARACHUTE_VY; }
      else if (target !== cx && !boxFree(state, cx + dir, c.y >> FP_SHIFT)) { c.vx = 0; c.vy = -PARACHUTE_VY; } // climb over the bank
      else if (target !== cx) { c.vx = dir * FP; c.vy = 0; }
      else { c.mode = CHEST_FALLING; c.vx = 0; c.vy = PARACHUTE_VY; }
    } else if (c.mode === CHEST_HOP) {
      // Hops toward its hiding place: a jump each time it is on the ground, MIMIC_HOPS at most.
      const cx = c.x >> FP_SHIFT;
      const grounded = !boxFree(state, cx, (c.y >> FP_SHIFT) + 1);
      const g = Math.max(1, Math.trunc(GRAVITY / state.map.gravityDiv));
      if (grounded && c.vy >= 0 && (Math.abs(cx - c.until) < 24 || c.hops <= 0)) { c.mode = CHEST_GROUND; c.vx = 0; endRun(state, c); }
      else if (grounded && c.vy >= 0) {
        // Hops of about 32 cells; the last one lands on the target (on the Moon too, where a hop flies three times as far).
        c.hops--;
        const air = Math.trunc((2 * MIMIC_HOP_VY) / g);
        c.vy = -MIMIC_HOP_VY;
        c.vx = Math.max(-(FP >> 1), Math.min(FP >> 1, Math.trunc(((c.until - cx) << FP_SHIFT) / air)));
      } else { c.vy += g; if (c.vy > MAX_FALL) c.vy = MAX_FALL; }
      if (centerLiquid(state, c)) { c.mode = CHEST_SHORE; c.vx = 0; c.vy = -PARACHUTE_VY; endRun(state, c); }
    } else {
      if (centerLiquid(state, c)) { c.mode = CHEST_SHORE; c.vx = 0; c.vy = -PARACHUTE_VY; }
      else {
        c.vy += Math.trunc(GRAVITY / state.map.gravityDiv);
        if (c.vy > MAX_FALL) c.vy = MAX_FALL;
        if (boxFree(state, c.x >> FP_SHIFT, (c.y >> FP_SHIFT) + 1) === false && c.vy > 0) { c.vy = 0; c.vx = 0; padsAndBelts(state, c); }
      }
    }
    moveVertical(state, c);
    if (moveHorizontal(state, c) && c.mode === CHEST_HOP) c.hops = Math.min(c.hops, MIMIC_STUCK_HOPS);
    if (c.y >> FP_SHIFT >= h) gone = true;

    if (!gone && touchesMat(state, c, Physique.ACID)) {
      // GDD: acid destroys it at once, its treasure is lost.
      state.events.push({ type: 'chest', chest: c.id, what: 'lost', content: c.content, unit: -1, x: c.x >> FP_SHIFT, y: c.y >> FP_SHIFT });
      gone = true;
    }
    if (!gone && touchesLava(state, c)) {
      c.heat++;
      if (c.heat >= CHEST_HEAT_TICKS) {
        state.events.push({ type: 'chest', chest: c.id, what: 'lost', content: c.content, unit: -1, x: c.x >> FP_SHIFT, y: c.y >> FP_SHIFT });
        gone = true;
      }
    }
    if (!gone && c.mode !== CHEST_HOP) {
      for (const u of state.units) {
        if (!touchesUnit(c, u) || !mayOpen(state, u, c)) continue;
        if (c.mimic) { if (!resting(state, c)) bite(state, c, u); } else { give(state, c, u); gone = true; }
        break;
      }
    }
    if (!gone && c.mode === CHEST_OPEN && (state.match ? state.match.turn > c.until : state.tick > c.until)) gone = true;
    if (gone) state.chests.splice(i, 1);
  }
}

/**
 * A chest at rest on a jump pad is thrown up (as a unit), on a conveyor belt it is carried (author's rule: the level editor).
 * The pad or the belt is under the chest when its row is the one right under the box, across the box.
 */
function padsAndBelts(state: GameState, c: Chest): void {
  const cx = c.x >> FP_SHIFT;
  const below = (c.y >> FP_SHIFT) + CHEST_H;
  for (const p of state.map.pads) {
    if (below !== p.y || cx + CHEST_W <= p.x0 || cx > p.x1) continue;
    c.vy = -PAD_VY;
    state.events.push({ type: 'pad', unit: -1, x: cx + (CHEST_W >> 1), y: below });
    return;
  }
  for (const b of state.map.belts) {
    if (below !== b.y || cx + CHEST_W <= b.x0 || cx > b.x1) continue;
    c.vx = b.dir * b.speed;
    return;
  }
}

/** A chest standing on the ground (the level editor): its box's top-left cell (x, y). */
export function groundChest(state: GameState, x: number, y: number, content: number, mimic: boolean): Chest {
  const c: Chest = { id: state.nextChest++, x: x << FP_SHIFT, y: y << FP_SHIFT, vx: 0, vy: 0, mode: CHEST_GROUND, content, heat: 0, until: 0, hops: 0, mimic };
  state.chests.push(c);
  return c;
}

/** Index of the chest whose box covers cell (x, y), or -1. */
export function chestAt(state: GameState, x: number, y: number): number {
  for (let i = 0; i < state.chests.length; i++) {
    const c = state.chests[i];
    const cx = c.x >> FP_SHIFT;
    const cy = c.y >> FP_SHIFT;
    if (x >= cx && x < cx + CHEST_W && y >= cy && y < cy + CHEST_H) return i;
  }
  return -1;
}

/** True if a mimic covers cell (x, y): bullets, rays and projectiles hit it (normal chests let them through). */
export function mimicAt(state: GameState, x: number, y: number): boolean {
  const i = chestAt(state, x, y);
  return i >= 0 && state.chests[i].mimic;
}

/** Destroyed, the mimic drops a rare weapon that anyone may pick for one turn. */
export function destroyMimic(state: GameState, index: number): void {
  const c = state.chests[index];
  // A rare weapon the host left in the chests (else any of those, else a medkit).
  let pool = RARE_WEAPONS.filter((wpn) => allowed(state, wpn));
  if (pool.length === 0) pool = CHEST_TABLE.map((e) => e.content).filter((k) => k !== CONTENT_MEDKIT && allowed(state, k));
  const content = pool.length > 0 ? pool[nextInt(state.world.rng, pool.length)] : CONTENT_MEDKIT;
  const until = state.match ? state.match.turn + 1 : state.tick + 10 * TICKS_PER_SECOND;
  state.chests[index] = { ...c, id: state.nextChest++, mode: CHEST_OPEN, content, mimic: false, until, hops: 0, vx: 0, vy: 0 };
  state.events.push({ type: 'chest', chest: c.id, what: 'blown', content, unit: -1, x: c.x >> FP_SHIFT, y: c.y >> FP_SHIFT });
}

/**
 * Author's addition: any weapon destroys a mimic, not only an explosion (a bullet, the laser, a projectile, the
 * meteor). True if there was one at (x, y).
 */
export function hitMimic(state: GameState, x: number, y: number): boolean {
  const i = chestAt(state, x, y);
  if (i < 0 || !state.chests[i].mimic) return false;
  destroyMimic(state, i);
  return true;
}

/** A blast throws the chests around; one hit in full (within a third of the radius) bursts open. */
export function blastChests(state: GameState, cx: number, cy: number, radius: number, kick: number): void {
  for (let ci = 0; ci < state.chests.length; ci++) {
    const c = state.chests[ci];
    if (c.mimic) {
      const dx = (c.x >> FP_SHIFT) + (CHEST_W >> 1) - cx;
      const dy = (c.y >> FP_SHIFT) + (CHEST_H >> 1) - cy;
      if (dx * dx + dy * dy < radius * radius) destroyMimic(state, ci);
      continue;
    }
    let dx = (c.x >> FP_SHIFT) + (CHEST_W >> 1) - cx;
    let dy = (c.y >> FP_SHIFT) + (CHEST_H >> 1) - cy;
    let d = isqrt(dx * dx + dy * dy);
    if (d >= radius) continue;
    if (d === 0) { dx = 0; dy = -1; d = 1; }
    const k = Math.trunc((kick * (radius - d)) / radius);
    c.vx += Math.trunc((k * dx) / d);
    c.vy += Math.trunc((k * dy) / d) - (FP >> 1);
    if (c.mode === CHEST_FALLING) c.mode = CHEST_GROUND;
    if (d * 3 < radius && c.mode !== CHEST_OPEN) {
      c.mode = CHEST_OPEN;
      c.until = state.match ? state.match.turn + 1 : state.tick + 10 * TICKS_PER_SECOND;
      state.events.push({ type: 'chest', chest: c.id, what: 'blown', content: c.content, unit: -1, x: c.x >> FP_SHIFT, y: c.y >> FP_SHIFT });
    }
  }
}
